<template>
  <div class="flex items-center gap-2">
    <UPopover :content="{ side: 'bottom', sideOffset: 8, collisionPadding: 12 }">
      <UButton
        :color="inputColor"
        variant="outline"
        size="md"
        class="min-w-0 flex-1"
        :disabled="disabled"
        :aria-label="$t('order.dateRange')"
      >
        <span class="min-w-0 flex-1 truncate text-left font-normal">{{ displayLabel }}</span>
      </UButton>

      <template #content>
        <ClientOnly>
          <UCalendar
            v-model="rangeValue"
            range
            :locale="calendarLocale"
            :week-starts-on="weekStartsOn"
            :number-of-months="2"
            class="p-2"
          />
        </ClientOnly>
      </template>
    </UPopover>

    <UPopover
      v-if="options.length"
      :content="{ side: 'bottom', align: 'end', sideOffset: 8, collisionPadding: 8 }"
    >
      <UButton
        color="neutral"
        variant="ghost"
        size="sm"
        square
        icon="i-lucide-sliders-horizontal"
        :aria-label="$t('general.preset')"
      />

      <template #content="{ close }">
        <div class="flex min-w-40 flex-col gap-0.5 p-1">
          <UButton
            v-for="(preset, index) in options"
            :key="index"
            color="neutral"
            variant="ghost"
            size="sm"
            block
            class="justify-start"
            @click="applyPreset(preset, close)"
          >
            {{ $t(preset.name) }}
          </UButton>
        </div>
      </template>
    </UPopover>
  </div>
</template>

<script setup lang="ts">
import { CalendarDate, type DateValue } from '@internationalized/date'
import { UButton, UCalendar, UPopover } from '#components'
import { augmenters } from '@/composables/useFieldAugmenters'
import { useCalendarFirstDayOfWeek } from '@/composables/useCalendarFirstDayOfWeek'

interface DateRangeModel {
  start: DateValue | undefined
  end: DateValue | undefined
}

const { $auth } = useNuxtApp()
const { locale, t } = useI18n()

interface Props {
  modelValue?: Record<string, any> | null
  augmenter?: Record<string, any>
  options?: any[]
  disabled?: boolean
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  disabled: false,
  status: null,
})

const augmenterResolved = computed(() => props.augmenter ?? augmenters.timestamp)

const inputColor = computed(() => {
  if (props.status === false || props.status === 'error') return 'error' as const
  if (props.status === 'warning') return 'warning' as const
  return 'neutral' as const
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'changeStart', value: any): void
  (e: 'changeFinish', value: any): void
}>()

// Composable normalises the 1-7 StartDayOfWeek enum to UCalendar's 0-6 scheme.
const weekStartsOn = useCalendarFirstDayOfWeek(() => ($auth as any)?.info?.location?.startDayOfWeek)

const calendarLocale = computed(() => String(locale.value ?? 'en').replace('_', '-'))

function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function calendarDateToDate(cd: DateValue): Date {
  return new Date(cd.year, cd.month - 1, cd.day)
}

function rawToCalendarDate(raw: any): CalendarDate | undefined {
  if (raw == null) return undefined
  const date = augmenterResolved.value?.input ? augmenterResolved.value.input(raw) : raw
  if (date instanceof Date) return dateToCalendarDate(date)
  return undefined
}

function calendarDateToRaw(cd: DateValue): any {
  const date = calendarDateToDate(cd)
  return augmenterResolved.value?.output ? augmenterResolved.value.output(date) : date
}

function formatCalendarDay(cd: CalendarDate): string {
  return new Intl.DateTimeFormat(calendarLocale.value, { dateStyle: 'medium' }).format(calendarDateToDate(cd))
}

const displayLabel = computed(() => {
  const start = rawToCalendarDate((props.modelValue as any)?.start)
  const end = rawToCalendarDate((props.modelValue as any)?.finish)
  if (!start && !end) {
    return t('order.dateRange')
  }
  if (start && end) {
    return `${formatCalendarDay(start)} – ${formatCalendarDay(end)}`
  }
  if (start) return formatCalendarDay(start)
  if (end) return formatCalendarDay(end)
  return t('order.dateRange')
})

const rangeValue = computed({
  get(): DateRangeModel {
    const start = rawToCalendarDate((props.modelValue as any)?.start)
    const end = rawToCalendarDate((props.modelValue as any)?.finish)
    return { start, end }
  },
  set(v: DateRangeModel) {
    const oldStart = (props.modelValue as any)?.start
    const oldFinish = (props.modelValue as any)?.finish
    const newStart = v.start ? calendarDateToRaw(v.start) : null
    const newFinish = v.end ? calendarDateToRaw(v.end) : null
    const updated = { ...(props.modelValue || {}), start: newStart, finish: newFinish }
    emit('update:modelValue', updated)
    if (oldStart !== newStart) {
      emit('change', { old: oldStart, new: newStart })
      emit('changeStart', { old: oldStart, new: newStart })
    }
    if (oldFinish !== newFinish) {
      emit('change', { old: oldFinish, new: newFinish })
      emit('changeFinish', { old: oldFinish, new: newFinish })
    }
  },
})

function applyPreset(preset: any, close?: () => void) {
  if (!preset) return

  const startDate = preset.value.start instanceof Date ? preset.value.start : new Date(preset.value.start * 1000)
  const finishDate = preset.value.finish instanceof Date ? preset.value.finish : new Date(preset.value.finish * 1000)

  const startRaw = augmenterResolved.value?.output ? augmenterResolved.value.output(startDate) : startDate
  const finishRaw = augmenterResolved.value?.output ? augmenterResolved.value.output(finishDate) : finishDate

  const startParams = { old: (props.modelValue as any)?.start, new: startRaw }
  const finishParams = { old: (props.modelValue as any)?.finish, new: finishRaw }

  emit('update:modelValue', { ...(props.modelValue || {}), start: startRaw, finish: finishRaw })
  emit('change', startParams)
  emit('changeStart', startParams)
  emit('change', finishParams)
  emit('changeFinish', finishParams)
  close?.()
}
</script>
